import React, { FC } from 'react';
import {
  InputAdornment,
  SxProps,
  TextField as MUITextField,
  Theme,
} from '@mui/material';
import { Control, Controller, FieldValues } from 'react-hook-form';
import InputMask from 'react-input-mask';

interface TextFieldProps {
  control: Control<FieldValues>;
  name: string;
  label?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  requiredText?: string;
  mask?: string;
  maskPlaceholder?: string | null;
  pattern?: RegExp;
  defaultValue?: string | number | null;
  type?: React.InputHTMLAttributes<unknown>['type'];
  disabled?: boolean;
  sx?: SxProps<Theme>;
  isUpperCaseValue?: boolean;
  endIcon?: JSX.Element;
}

const TextField: FC<TextFieldProps> = ({
  control,
  name,
  label,
  required,
  size,
  requiredText = '',
  mask,
  maskPlaceholder,
  pattern,
  defaultValue,
  type,
  disabled,
  sx,
  isUpperCaseValue,
  endIcon,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ''}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <InputMask
          mask={mask || ''}
          maskPlaceholder={maskPlaceholder}
          onChange={onChange}
          value={isUpperCaseValue ? value.toUpperCase() : value}
          alwaysShowMask={false}
          disabled={disabled}
        >
          <MUITextField
            sx={{
              ...sx,
              '& .MuiOutlinedInput-input': {
                padding: size === 'small' ? '11px 14px' : '13.5px 14px',
              },
              '& .MuiInputLabel-root': {
                color: '#BDBDBD',
                lineHeight: size === 'small' ? '28px' : '20px',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: 'primary.contrastText',
              },
              '& .MuiInputLabel-shrink': {
                color: 'primary.contrastText',
                backgroundColor: 'primary.main',
                padding: '0px 5px',
                left: '-5px',
                top: '2px',
                fontWeight: '600',
                fontSize: '13px',
                lineHeight: '20px',
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                '& fieldset': {
                  borderColor: 'primary.main',
                  '& legend': {
                    width: '38px',
                  },
                },
                '&:hover fieldset': {
                  border: '1px solid',
                  borderColor: 'secondary.main',
                },
                '&.Mui-focused fieldset': {
                  border: '1px solid',
                  borderColor: 'secondary.main',
                },
              },
              '& .MuiFormHelperText-root': {
                position: 'absolute',
                top: '45px',
                left: '0',
              },
            }}
            fullWidth
            size={size}
            label={label || ''}
            variant="outlined"
            error={!!error}
            type={type}
            helperText={error ? error.message : null}
            InputProps={{
              endAdornment: endIcon && (
                <InputAdornment position="start">{endIcon}</InputAdornment>
              ),
            }}
          />
        </InputMask>
      )}
      rules={{
        required: required || requiredText,
        pattern,
      }}
    />
  );
};

export default TextField;
